import React, { useEffect, useState } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { useTimeRange } from "./hooks/useTimeRange";
import { useDashboardData } from "./hooks/useDashboardData";
import { useProductAnalysis } from "./hooks/useProductAnalysis";
import SummaryCards from "./components/summary-cards/SummaryCards";
import EmptyState from "./components/EmptyState";
import "./Resdeshboard.css";
import { PageTitle } from "src/shared/PageTitle";
import LoadingScreen from "src/shared/components/LoadingScreen";
import GenerateDailyReport from "src/business-types/laundry/modules/dashboard/GenerateReport";
import { pageTitle } from "src/shared/utils/brand";
import TimeRangeControls from "./components/Controls/TimeRangeControls";
import SalesChart from "./components/Charts/SalesChart";
import PaymentDistributionChart from "./components/Charts/PaymentDistributionChart";
import TopSellingItemsChart from "./components/Charts/TopSellingItemsChart";
import ProductPerformanceChart from "./components/Charts/ProductPerformanceChart";

const Dashboard: React.FC = () => {
  const { timeRange, customStartDate, customEndDate, handleTimeRangeChange, setCustomStartDate, setCustomEndDate } = useTimeRange();

  const { dashboardData, loading, fetchDashboardData } = useDashboardData(timeRange, customStartDate, customEndDate);

  const { selectedProduct, productSalesData, handleProductSelect } = useProductAnalysis();
  const [reportGenerated, setReportGenerated] = useState(false);

  const handleRefresh = () => {
    fetchDashboardData();
  };
  useEffect(() => {
    document.title = pageTitle("Dashboard");
  }, []);

  const handleProductSelection = (productName: string) => {
    if (dashboardData) {
      handleProductSelect(productName, dashboardData.recentOrders, dashboardData.timeSeries, timeRange);
    }
  };

  return (
    <div className="res-dashboard-container">
      <div>
        {/* <Header Header={"Dashboard"} OldOrders={oldOrders} />
        <div className="res-dashboard-layout">
          <Menu /> */}
        <PageTitle title="- Dashboard" />
        <Box className="res-main-content">
          {/* Header Section */}
          <Box className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            {/* Left: Title */}
            <Box>
              <Typography variant="h4" className="font-semibold text-slate-800">
                Restaurant Analytics
              </Typography>
              <Typography variant="subtitle1" className="text-slate-500">
                Performance overview and key metrics
              </Typography>
            </Box>

            {/* Center: Success message */}
            {reportGenerated && (
              <div className="flex-1 max-w-xl mx-4 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700 text-center">
                The today report has been sent to your email. For date-wise reports, please visit the Report tab.
              </div>
            )}

            {/* Right: Actions (button → date range) */}
            <Box className="flex items-center gap-3">
              <GenerateDailyReport onReportGenerated={() => setReportGenerated(true)} />

              <TimeRangeControls
                timeRange={timeRange}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onTimeRangeChange={handleTimeRangeChange}
                onCustomStartDateChange={setCustomStartDate}
                onCustomEndDateChange={setCustomEndDate}
                onRefresh={handleRefresh}
              />
            </Box>
          </Box>

          {loading ? (
            <LoadingScreen />
          ) : dashboardData ? (
            <>
              <SummaryCards dashboardData={dashboardData} />

              {/* Charts Section */}
              <Grid container spacing={3} className="res-charts-section">
                {/* Sales Over Time */}
                <Grid item xs={12} md={8}>
                  <SalesChart timeSeries={dashboardData.timeSeries} />
                </Grid>

                {/* Payment Methods */}
                <Grid item xs={12} md={4}>
                  <PaymentDistributionChart paymentMethods={dashboardData.paymentMethods} />
                </Grid>

                {/* Popular Items */}
                <Grid item xs={12} md={6}>
                  <TopSellingItemsChart popularItems={dashboardData.popularItems} />
                </Grid>

                {/* Product Sales Over Time */}
                <Grid item xs={12} md={6}>
                  <ProductPerformanceChart popularItems={dashboardData.popularItems} selectedProduct={selectedProduct} productSalesData={productSalesData} onProductSelect={handleProductSelection} />
                </Grid>
              </Grid>
            </>
          ) : (
            <EmptyState />
          )}
        </Box>
        {/* </div> */}
      </div>
    </div>
  );
};

export default Dashboard;
